"use client";

import { useCallback, useState } from "react";
import { useToast } from "@wtc/ui";
import type { RankedCandidate, CompanyData } from "@/lib/research/api-types";
import type { ApolloContact } from "@/lib/apollo/types";
import type { ResearchImportResult, ResearchModalStep } from "./types";

interface UseResearchCompanyModalOptions {
  linkedCompanyId: string | null;
  onClose: () => void;
}

export function useResearchCompanyModal({
  linkedCompanyId,
  onClose,
}: UseResearchCompanyModalOptions) {
  const { toast } = useToast();

  const [step, setStep] = useState<ResearchModalStep>("search");
  const [searchQuery, setSearchQuery] = useState("");
  const [candidates, setCandidates] = useState<RankedCandidate[]>([]);
  const [selectedCandidate, setSelectedCandidate] = useState<RankedCandidate | null>(null);
  const [contacts, setContacts] = useState<ApolloContact[]>([]);
  const [selectedContacts, setSelectedContacts] = useState<Set<string>>(new Set());
  const [importResults, setImportResults] = useState<ResearchImportResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [companyData, setCompanyData] = useState<CompanyData | null>(null);

  const performSearch = useCallback(async (query: string) => {
    if (!query.trim()) {
      setError("Please enter a search query");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch("/api/companies/research", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          step: "search",
          query: query,
          companyId: linkedCompanyId || "",
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Search failed");
      }

      const data = await response.json();
      setCandidates(data.candidates || []);

      if (data.candidates.length === 0) {
        setError("No companies found. Try a different search query.");
      } else {
        setStep("company-selection");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Search failed");
    } finally {
      setLoading(false);
    }
  }, [linkedCompanyId]);

  const handleSearch = useCallback(() => {
    void performSearch(searchQuery);
  }, [searchQuery, performSearch]);

  const handleSelectCompany = useCallback(async (candidate: RankedCandidate) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch("/api/companies/research", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          step: "find-contacts",
          apolloCompanyId: candidate.id,
          companyId: linkedCompanyId || "",
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Failed to load contacts");
      }

      const data = await response.json();
      setSelectedCandidate(candidate);
      setContacts(data.contacts || []);
      setCompanyData(data.companyData);
      setSelectedContacts(new Set());
      setStep("contact-selection");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load contacts");
    } finally {
      setLoading(false);
    }
  }, [linkedCompanyId]);

  const toggleContactSelection = useCallback((contactId: string) => {
    setSelectedContacts((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(contactId)) {
        newSet.delete(contactId);
      } else {
        newSet.add(contactId);
      }
      return newSet;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedContacts((prev) => {
      if (prev.size === contacts.length) {
        return new Set();
      }
      return new Set(contacts.map((c) => c.id));
    });
  }, [contacts]);

  const handleImportContacts = useCallback(async () => {
    if (selectedContacts.size === 0) {
      setError("Please select at least one contact");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const contactsToImport = contacts.filter((c) => selectedContacts.has(c.id));

      if (!companyData?.id) {
        throw new Error("Company data not available");
      }

      const response = await fetch("/api/companies/research", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          step: "import-contacts",
          contacts: contactsToImport.map((c) => ({
            id: c.id,
            first_name: c.first_name,
            last_name: c.last_name,
            email: c.email,
            phone_number: c.phone_number,
            job_title: c.job_title,
            job_level: c.job_level,
          })),
          companyId: linkedCompanyId || "",
          companyData: companyData,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Import failed");
      }

      const data = await response.json();
      setImportResults(data.results || []);
      setStep("import-results");
      toast({
        title: "Import Complete",
        description: data.message,
        variant: data.failed > 0 ? "destructive" : "default",
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Import failed");
    } finally {
      setLoading(false);
    }
  }, [selectedContacts, contacts, companyData, linkedCompanyId, toast]);

  const handleClose = useCallback(() => {
    onClose();
    setStep("search");
    setSearchQuery("");
    setCandidates([]);
    setSelectedCandidate(null);
    setContacts([]);
    setSelectedContacts(new Set());
    setImportResults([]);
    setError(null);
  }, [onClose]);

  return {
    step,
    setStep,
    searchQuery,
    setSearchQuery,
    candidates,
    selectedCandidate,
    contacts,
    selectedContacts,
    importResults,
    loading,
    error,
    companyData,
    performSearch,
    handleSearch,
    handleSelectCompany,
    toggleContactSelection,
    toggleSelectAll,
    handleImportContacts,
    handleClose,
  };
}
